import { PencilSimpleIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import { captureException } from '@sentry/react';
import { FormDialog } from '../../../../components/dialog/FormDialog';
import { ISimpleSelectItem } from '../../../../components/select/SimpleSelect';
import { SimpleSelectField } from '../../../../components/select/SimpleSelectField';
import { TextAreaField } from '../../../../components/textarea/TextAreaField';
import { useTranslation } from '../../../../contexts/translation-context';
import { Document, OrderComplaintType } from 'src/types/enums';
import { linkOrderComplaintDocument, unlinkOrderComplaintDocument, updateOrderComplaint } from '../../models';
import type { OrderComplaintTypeValue } from '../../endpoints/shared.types';
import { getDisplayError } from '../../../../utils/get-display-error';
import { ComplaintAttachmentUpload } from './ComplaintAttachmentUpload';
import { useOrder } from './orderContext';

const schema = Yup.object().shape({
  type: Yup.object().required('errors.required'),
  content: Yup.string().required('errors.required'),
  attachments: Yup.array(),
});

export interface IEditOrderComplaintDialogProps {
  complaint: {
    id: string;
    type: OrderComplaintTypeValue;
    content: string;
    attachments: Array<{
      id: string;
      createdAt: string;
      document: {
        id: string;
        hash: string;
        name: string;
      };
    }>;
  };
  isDisabled?: boolean;
}

interface EditOrderComplaintDialogValues {
  type: ISimpleSelectItem;
  content: string;
  attachments: Document[];
}

export const EditOrderComplaintDialog: React.FC<IEditOrderComplaintDialogProps> = (props) => {
  const { complaint } = props;
  const { i18n } = useTranslation();
  const { refreshData } = useOrder();

  const handleSubmit = async (values: EditOrderComplaintDialogValues) => {
    try {
      // Update complaint basic data
      await updateOrderComplaint(complaint.id, {
        type: values.type.key as OrderComplaintTypeValue,
        content: values.content,
      });

      // Handle attachment changes
      const currentAttachmentIds = new Set(complaint.attachments.map((a) => a.document.id));
      const newAttachmentIds = new Set(values.attachments.map((doc) => doc.id));

      // Unlink removed attachments
      for (const currentId of currentAttachmentIds) {
        if (!newAttachmentIds.has(currentId)) {
          await unlinkOrderComplaintDocument(complaint.id, currentId);
        }
      }

      // Link new attachments
      for (const newId of newAttachmentIds) {
        if (!currentAttachmentIds.has(newId)) {
          await linkOrderComplaintDocument(complaint.id, newId);
        }
      }

      toast.success('Klacht bijgewerkt');
      await refreshData();
    } catch (err) {
      captureException(err);
      console.error('Failed to update complaint', err);
      toast.error('Kon klacht niet bijwerken: ' + getDisplayError(err));
    }
  };

  const items: ISimpleSelectItem[] = Object.values(OrderComplaintType).map((type) => ({
    key: type,
    name: i18n(`orderComplaintType.${type}`),
  }));

  const initialValues: EditOrderComplaintDialogValues = useMemo(() => {
    return {
      type: items.find((v) => v.key === complaint.type) || items[0],
      content: complaint.content,
      // Map to Document type with required fields
      attachments: complaint.attachments.map(
        (a) =>
          ({
            id: a.document.id,
            hash: a.document.hash,
            name: a.document.name,
            createdAt: a.createdAt,
            filename: a.document.name,
          }) as Document,
      ),
    };
  }, [complaint, items]);

  return (
    <FormDialog<EditOrderComplaintDialogValues>
      triggerText={<PencilSimpleIcon className="button-icon" />}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
      title="Klacht bewerken"
      submitText="Bijwerken"
    >
      <Dialog.Description className="mb-4">Bewerk de klacht details en bijlagen.</Dialog.Description>

      <SimpleSelectField items={items} labelText="Reden" name="type" />

      <TextAreaField labelText="Bericht" name="content" />

      <ComplaintAttachmentUpload />
    </FormDialog>
  );
};
